import React from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Meta from '../components/Meta';
import { Helmet } from 'react-helmet';
import { 
  faShieldAlt, 
  faUserShield, 
  faFingerprint, 
  faLock,
  faUserLock,
  faExchangeAlt,
  faUserEdit,
  faEnvelope,
  faFileContract,
  faServer,
  faArrowRight,
  faCheck,
  faStar,
  faGraduationCap,
  faEyeSlash,
  faHandshake,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import '../styles/Policy.scss';
import ContentRecommendations from '../components/ContentRecommendations';
import { Link } from 'react-router-dom';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: "easeOut"
    }
  })
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 15
    }
  }
};

export default function PrivacyPolicy() {
  return (
    <div className="policy-page privacy-policy-page">
      <Meta
        title="Privacy Policy | Homework Guy"
        description="Read our privacy policy to understand how we collect, use, and protect your personal information. We are committed to your privacy and data security."
        canonicalUrl="https://homeworkguy.org/privacy-policy"
      />
      
      <section className="policy-header">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-center">Privacy Policy</h1>
            <div className="header-underline mx-auto mb-4"></div>
            <p className="lead text-center">Your privacy is our top priority. This policy outlines how we collect, use, and protect your information.</p>
          </motion.div>
        </Container>
      </section>

      <section className="highlights-section py-5">
        <Container>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Row className="gy-4 text-center">
              <Col lg={3} md={6}>
                <motion.div
                  className="highlight-card py-4 px-3 h-100"
                  variants={itemVariants}
                  whileHover={{ y: -10, boxShadow: "0 10px 25px rgba(0,0,0,0.1)" }}
                >
                  <div className="highlight-icon mb-3">
                    <FontAwesomeIcon icon={faShieldAlt} size="2x" />
                  </div>
                  <h3>Bank-Level Security</h3>
                  <p>256-bit SSL encryption protects all your data and transactions</p>
                </motion.div>
              </Col>
              
              <Col lg={3} md={6}>
                <motion.div
                  className="highlight-card py-4 px-3 h-100"
                  variants={itemVariants}
                  whileHover={{ y: -10, boxShadow: "0 10px 25px rgba(0,0,0,0.1)" }}
                >
                  <div className="highlight-icon mb-3">
                    <FontAwesomeIcon icon={faEyeSlash} size="2x" />
                  </div>
                  <h3>Complete Anonymity</h3>
                  <p>Your identity is never revealed to tutors, schools, or any third party</p>
                </motion.div>
              </Col>
              
              <Col lg={3} md={6}>
                <motion.div
                  className="highlight-card py-4 px-3 h-100"
                  variants={itemVariants}
                  whileHover={{ y: -10, boxShadow: "0 10px 25px rgba(0,0,0,0.1)" }}
                >
                  <div className="highlight-icon mb-3">
                    <FontAwesomeIcon icon={faUserLock} size="2x" />
                  </div>
                  <h3>Credential Safety</h3>
                  <p>Login credentials are encrypted and only shared with your assigned expert</p>
                </motion.div>
              </Col>
              
              <Col lg={3} md={6}>
                <motion.div
                  className="highlight-card py-4 px-3 h-100"
                  variants={itemVariants}
                  whileHover={{ y: -10, boxShadow: "0 10px 25px rgba(0,0,0,0.1)" }}
                >
                  <div className="highlight-icon mb-3">
                    <FontAwesomeIcon icon={faExchangeAlt} size="2x" />
                  </div>
                  <h3>Zero Data Sharing</h3>
                  <p>We never share, sell, or expose your information to third parties</p>
                </motion.div>
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Trust Stats Section */}
      <section className="trust-stats-section py-4" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
        <Container>
          <Row className="align-items-center text-center text-white">
            <Col md={3} sm={6} className="py-3">
              <div style={{ fontSize: '2rem', fontWeight: '700' }}>50,000+</div>
              <div style={{ fontSize: '0.9rem', opacity: '0.8' }}>Students Trust Us</div>
            </Col>
            <Col md={3} sm={6} className="py-3">
              <div style={{ fontSize: '2rem', fontWeight: '700' }}>0</div>
              <div style={{ fontSize: '0.9rem', opacity: '0.8' }}>Data Breaches Ever</div>
            </Col>
            <Col md={3} sm={6} className="py-3">
              <div style={{ fontSize: '2rem', fontWeight: '700' }}>100%</div>
              <div style={{ fontSize: '0.9rem', opacity: '0.8' }}>Confidential Service</div>
            </Col>
            <Col md={3} sm={6} className="py-3">
              <div style={{ fontSize: '2rem', fontWeight: '700' }}>24/7</div>
              <div style={{ fontSize: '0.9rem', opacity: '0.8' }}>Security Monitoring</div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="policy-content">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10}>
              <motion.div 
                className="policy-card mb-5"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeIn}
              >
                <div className="guarantee-header">
                  <div className="icon-wrapper">
                    <FontAwesomeIcon icon={faFingerprint} className="guarantee-icon" />
                  </div>
                  <h2>Information We Collect</h2>
                </div>
                <div className="card-content">
                  <p className="mb-4">
                    We collect information that you provide directly to us when using our academic assistance services:
                  </p>
                  
                  <div className="trust-feature mb-4">
                    <div className="feature-icon">
                      <FontAwesomeIcon icon={faUserShield} />
                    </div>
                    <div className="feature-content">
                      <h4>Personal Information</h4>
                      <p>
                        This includes your name, email address, phone number, and academic institution details. We collect this information to provide our services and communicate with you about your orders.
                      </p>
                    </div>
                  </div>
                  
                  <div className="trust-feature mb-4">
                    <div className="feature-icon">
                      <FontAwesomeIcon icon={faFileContract} />
                    </div>
                    <div className="feature-content">
                      <h4>Academic Information</h4>
                      <p>
                        We collect details about your courses, assignments, and academic requirements to properly fulfill your requests and provide appropriate assistance.
                      </p>
                    </div>
                  </div>
                  
                  <div className="trust-feature">
                    <div className="feature-icon">
                      <FontAwesomeIcon icon={faServer} />
                    </div>
                    <div className="feature-content">
                      <h4>Technical Information</h4>
                      <p>
                        We automatically collect certain information when you visit our website, including your IP address, browser type, and device information. This helps us optimize our services and ensure website security.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div 
                className="policy-card mb-5"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeIn}
                custom={1}
              >
                <div className="guarantee-header">
                  <div className="icon-wrapper">
                    <FontAwesomeIcon icon={faLock} className="guarantee-icon" />
                  </div>
                  <h2>How We Protect Your Information</h2>
                </div>
                <div className="card-content">
                  <p className="mb-4">
                    We implement robust security measures to safeguard your personal information:
                  </p>
                  
                  <ul className="privacy-list">
                    <li>
                      <strong>Encryption:</strong> All sensitive data is encrypted using industry-standard protocols to protect against unauthorized access.
                    </li>
                    <li>
                      <strong>Secure Payments:</strong> Payment information is processed through trusted third-party providers with PCI DSS compliance.
                    </li>
                    <li>
                      <strong>Access Controls:</strong> We limit access to personal information to only authorized personnel who need it to provide services.
                    </li>
                    <li>
                      <strong>Regular Audits:</strong> We conduct regular security audits to ensure our systems remain secure and up-to-date.
                    </li>
                    <li>
                      <strong>Data Retention:</strong> We only retain your information for as long as necessary to provide services or as required by law.
                    </li>
                  </ul>
                </div>
              </motion.div>

              <motion.div 
                className="policy-card mb-5"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeIn}
                custom={2}
              >
                <div className="guarantee-header">
                  <div className="icon-wrapper">
                    <FontAwesomeIcon icon={faUserEdit} className="guarantee-icon" />
                  </div>
                  <h2>Your Rights</h2>
                </div>
                <div className="card-content">
                  <p className="mb-4">
                    You have several rights regarding your personal information:
                  </p>
                  
                  <div className="process-steps">
                    <div className="process-step">
                      <div className="step-number">1</div>
                      <div className="step-content">
                        <h5>Access Your Data</h5>
                        <p>You can request a copy of the personal information we hold about you.</p>
                      </div>
                    </div>
                    
                    <div className="process-step">
                      <div className="step-number">2</div>
                      <div className="step-content">
                        <h5>Correct Your Data</h5>
                        <p>You can request that we correct any inaccurate or incomplete information about you.</p>
                      </div>
                    </div>
                    
                    <div className="process-step">
                      <div className="step-number">3</div>
                      <div className="step-content">
                        <h5>Delete Your Data</h5>
                        <p>You can request that we delete your personal information in certain circumstances.</p>
                      </div>
                    </div>
                    
                    <div className="process-step">
                      <div className="step-number">4</div>
                      <div className="step-content">
                        <h5>Restrict Processing</h5>
                        <p>You can request that we restrict the processing of your information in certain circumstances.</p>
                      </div>
                    </div>
                    
                    <div className="process-step">
                      <div className="step-number">5</div>
                      <div className="step-content">
                        <h5>Opt-Out</h5>
                        <p>You can opt-out of marketing communications at any time by clicking the "unsubscribe" link in our emails.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div 
                className="policy-card mb-5"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeIn}
                custom={4}
              >
                <div className="guarantee-header">
                  <div className="icon-wrapper">
                    <FontAwesomeIcon icon={faFileContract} className="guarantee-icon" />
                  </div>
                  <h2>Policy Updates</h2>
                </div>
                <div className="card-content">
                  <p className="mb-4">
                    We may update this privacy policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by:
                  </p>
                  
                  <ul className="privacy-list">
                    <li>Posting the new policy on this page</li>
                    <li>Updating the "Last Updated" date at the bottom of this page</li>
                    <li>Sending an email notification to users if significant changes are made</li>
                  </ul>
                  
                  <div className="effective-date-wrapper mt-4 text-center">
                    <div className="effective-date d-inline-block py-2 px-4 rounded">
                      <strong>Last Updated:</strong> {new Date().toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Student Confidentiality Promise Section */}
              <motion.div 
                className="student-promise-section mb-5 p-4"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeIn}
                custom={5}
                style={{
                  background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                  borderRadius: '15px',
                  color: 'white'
                }}
              >
                <Row className="align-items-center">
                  <Col lg={8}>
                    <h3 className="mb-3" style={{ fontWeight: '700' }}>
                      <FontAwesomeIcon icon={faHandshake} className="me-2" />
                      Our Promise to Students
                    </h3>
                    <p className="mb-3" style={{ fontSize: '1.05rem', opacity: '0.95' }}>
                      We understand the importance of discretion when using academic assistance services. That's why we've built our entire platform around protecting your privacy:
                    </p>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'flex-start' }}>
                        <FontAwesomeIcon icon={faCheckCircle} className="me-2 mt-1" style={{ flexShrink: 0 }} />
                        <span><strong>Your school will never know</strong> – We never contact educational institutions or share any student data</span>
                      </li>
                      <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'flex-start' }}>
                        <FontAwesomeIcon icon={faCheckCircle} className="me-2 mt-1" style={{ flexShrink: 0 }} />
                        <span><strong>Anonymous billing</strong> – Our name won't appear on bank statements in any academic-related way</span>
                      </li>
                      <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'flex-start' }}>
                        <FontAwesomeIcon icon={faCheckCircle} className="me-2 mt-1" style={{ flexShrink: 0 }} />
                        <span><strong>Secure credential handling</strong> – If you share login details, they're encrypted and only accessed by your assigned expert</span>
                      </li>
                      <li style={{ display: 'flex', alignItems: 'flex-start' }}>
                        <FontAwesomeIcon icon={faCheckCircle} className="me-2 mt-1" style={{ flexShrink: 0 }} />
                        <span><strong>No trace policy</strong> – After service completion, you can request complete data deletion</span>
                      </li>
                    </ul>
                  </Col>
                  <Col lg={4} className="text-center mt-4 mt-lg-0">
                    <div style={{ 
                      background: 'rgba(255,255,255,0.2)', 
                      borderRadius: '15px', 
                      padding: '25px',
                      backdropFilter: 'blur(10px)'
                    }}>
                      <FontAwesomeIcon icon={faGraduationCap} size="3x" className="mb-3" />
                      <h4 style={{ fontWeight: '600' }}>50,000+ Students</h4>
                      <p style={{ marginBottom: 0, opacity: '0.9' }}>Trust us with their academic success and privacy</p>
                    </div>
                  </Col>
                </Row>
              </motion.div>

              {/* Testimonial */}
              <motion.div 
                className="privacy-testimonial mb-5 p-4 text-center"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeIn}
                custom={6}
                style={{
                  background: '#f8f9fa',
                  borderRadius: '15px',
                  borderLeft: '4px solid #3a47d5'
                }}
              >
                <div className="stars mb-2" style={{ color: '#ffc107' }}>
                  <FontAwesomeIcon icon={faStar} />
                  <FontAwesomeIcon icon={faStar} />
                  <FontAwesomeIcon icon={faStar} />
                  <FontAwesomeIcon icon={faStar} />
                  <FontAwesomeIcon icon={faStar} />
                </div>
                <p style={{ fontSize: '1.1rem', fontStyle: 'italic', color: '#555', marginBottom: '10px' }}>
                  "I was nervous about using an online class service, but Homework Guy's privacy practices gave me confidence. They handled everything discreetly, and I never had any issues. My school never knew, and my information stayed completely private."
                </p>
                <div style={{ fontWeight: '600', color: '#333' }}>— Jessica T., Business Administration Student</div>
              </motion.div>

              {/* CTA Section */}
              <section className="cta-section">
                <Container>
                  <div className="cta-container">
                    <div className="cta-content">
                      <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeIn}
                        custom={4}
                      >
                        <h2>Ready to Get Started with Complete Confidence?</h2>
                        <p>Your privacy is protected. Your data is secure. Your success is our mission.</p>
                        <div className="d-flex flex-wrap justify-content-center gap-3 mb-4">
                          <Link to="/order">
                            <Button 
                              variant="warning" 
                              size="lg" 
                              className="main-cta-btn"
                            >
                              Start Your Order <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                            </Button>
                          </Link>
                          <Button 
                            variant="outline-light" 
                            size="lg"
                            href="/contact"
                          >
                            Contact Privacy Team
                          </Button>
                        </div>
                        <div className="cta-features">
                          <div className="feature">
                            <FontAwesomeIcon icon={faCheck} /> $0 Upfront Payment
                          </div>
                          <div className="feature">
                            <FontAwesomeIcon icon={faCheck} /> 100% Confidential
                          </div>
                          <div className="feature">
                            <FontAwesomeIcon icon={faCheck} /> Grade Guarantee
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </Container>
              </section>
            </Col>
          </Row>
        </Container>
      </section>
      
      <style>{`
        .privacy-policy-page {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', sans-serif;
        }
        
        .privacy-list {
          padding-left: 1.5rem;
        }
        
        .privacy-list li {
          margin-bottom: 1rem;
          line-height: 1.6;
        }
        
        .privacy-scenario {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 1.5rem;
          margin-bottom: 1rem;
          border-left: 4px solid #3a47d5;
        }
        
        .privacy-scenario h5 {
          color: #3a47d5;
          margin-bottom: 0.5rem;
        }
        
        .effective-date-wrapper {
          margin-top: 2rem;
        }
        
        .effective-date {
          background: #f0f4ff;
          color: #3a47d5;
          font-style: italic;
          border: 1px solid rgba(58, 71, 213, 0.2);
        }
        
        .text-success {
          color: #28a745;
        }
        
        .cta-content h2 {
          color: white;
          font-weight: 700;
          font-size: 2rem;
          margin-bottom: 1rem;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }
        
        .cta-content p {
          color: white;
          font-size: 1.1rem;
          margin-bottom: 1.5rem;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
        }
      `}</style>
    <ContentRecommendations currentPath="/privacy-policy" count={3} title="Recommended Resources" /></div>
  );
} 